/**
 * My bookings — redesign v3 "My bookings + venue" frame: combined amenity +
 * venue bookings (API already returns upcoming-first, then past) with
 * cancel, plus a "Venues for functions" browse row (arch-tinted tiles) that
 * jumps into the full venue-booking flow — same GET /api/bookings/venues
 * already used by venue-booking.tsx, just surfaced here as a shortcut.
 * Wired: GET /api/bookings/mine, GET /api/bookings/venues,
 * POST /api/bookings/<type>/<id>/cancel (optimistic, reverts on failure).
 * Port of design/source/screens-account.jsx MyBookings.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { radius, space, type ThemeColors } from '@bero/tokens';
import {
  amenityIcon, backOr, CardSkeletons, inr, longDate, venueIcon, venuePillStatus,
} from '@/components/money/bits';
import {
  cancelBooking, errorMessage, fetchMyBookings, fetchVenues, useAsync, type MyBooking, type Venue,
} from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Pill, Screen, TopBar } from '@/ui';

/** Tint pair per venue (arch tile + silhouette) — cycles through the
 * screen's neutral/primary/accent palette without repeating a full amber
 * surface (accent is used at most once, on the second tile). */
const TILE_TINTS = [
  { tile: 'canvas1', arch: 'primary400' },
  { tile: 'accent100', arch: 'accent500' },
  { tile: 'canvas2', arch: 'borderFirm' },
] as const;

function isUpcoming(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() >= today.getTime();
}

export default function MyBookingsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { data: bookings, loading, error, refetch, mutate } = useAsync(fetchMyBookings, []);
  const { data: venues } = useAsync(fetchVenues, []);
  const [cancellingKey, setCancellingKey] = React.useState<string | null>(null);
  const [confirmingKey, setConfirmingKey] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const cancel = async (b: MyBooking) => {
    const key = `${b.kind}-${b.id}`;
    if (cancellingKey) return;
    const prev = bookings;
    setNotice(null);
    setConfirmingKey(null);
    setCancellingKey(key);
    mutate((list) => (list ?? []).filter((x) => !(x.kind === b.kind && x.id === b.id)));
    try {
      await cancelBooking(b.kind, b.id);
    } catch (e) {
      mutate(() => prev ?? null);
      setNotice(errorMessage(e, t('mb_cancel_error')));
    } finally {
      setCancellingKey(null);
    }
  };

  /** First tap asks for confirmation; a second tap on "Yes, cancel" actually cancels. */
  const requestCancel = (b: MyBooking) => {
    const key = `${b.kind}-${b.id}`;
    setNotice(null);
    setConfirmingKey(key);
  };

  const upcoming = (bookings ?? []).filter((b) => isUpcoming(b.when));
  const past = (bookings ?? []).filter((b) => !isUpcoming(b.when));

  return (
    <Screen scroll={false}>
      <TopBar title={t('am_my_bookings')} onBack={backOr('/(tabs)/home')} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {notice ? <AppText variant="bodySm" tone="danger" style={{ marginBottom: space.s3 }}>{notice}</AppText> : null}
        {loading ? (
          <CardSkeletons count={3} h={88} />
        ) : error ? (
          <EmptyState
            title={t('mb_err_title')}
            body={t('mb_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : !bookings || bookings.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
            title={t('mb_empty_title')}
            body={t('mb_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s5 }}>
            {upcoming.length > 0 ? (
              <View style={{ gap: space.s3 }}>
                <AppText variant="label" tone="tertiary">{t('ct_upcoming')}</AppText>
                {upcoming.map((b) => (
                  <BookingRow
                    key={`${b.kind}-${b.id}`}
                    booking={b}
                    onRequestCancel={() => requestCancel(b)}
                    onConfirmCancel={() => void cancel(b)}
                    onKeepCancel={() => setConfirmingKey(null)}
                    confirming={confirmingKey === `${b.kind}-${b.id}`}
                    cancelling={cancellingKey === `${b.kind}-${b.id}`}
                  />
                ))}
              </View>
            ) : null}

            {venues && venues.length > 0 ? (
              <View style={{ gap: space.s3 }}>
                <AppText variant="label" tone="tertiary">{t('mb_venues_for_functions')}</AppText>
                <View style={{ gap: space.s3 }}>
                  {venues.map((v, i) => <VenueTile key={v.id} venue={v} tint={TILE_TINTS[i % TILE_TINTS.length]} />)}
                </View>
              </View>
            ) : null}

            {past.length > 0 ? (
              <View style={{ gap: space.s3 }}>
                <AppText variant="label" tone="tertiary">{t('ct_past')}</AppText>
                {past.map((b) => (
                  <BookingRow
                    key={`${b.kind}-${b.id}`}
                    booking={b}
                    onRequestCancel={() => {}}
                    onConfirmCancel={() => {}}
                    onKeepCancel={() => {}}
                    confirming={false}
                    cancelling={false}
                    readOnly
                  />
                ))}
              </View>
            ) : null}
          </View>
        )}
      </Screen>
    </Screen>
  );
}

function VenueTile({ venue, tint }: { venue: Venue; tint: { tile: keyof ThemeColors; arch: keyof ThemeColors } }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push('/venue-booking')}
      style={({ pressed }) => ({
        borderRadius: radius.xl, overflow: 'hidden',
        borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
        backgroundColor: pressed ? colors.canvas1 : colors.canvas0,
      })}
    >
      <View style={{ height: 64, backgroundColor: colors[tint.tile], alignItems: 'center', justifyContent: 'center' }}>
        <View style={{
          width: 44, height: 52, borderRadius: 22, backgroundColor: colors[tint.arch], opacity: 0.55,
          borderBottomLeftRadius: 6, borderBottomRightRadius: 6,
        }} />
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', padding: space.s3, gap: space.s2 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodyMd" weight="600">{venue.name}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
            {t('mb_up_to_guests', { n: venue.capacity })} · {t('mb_days_ahead', { n: venue.advanceDays })}
          </AppText>
        </View>
        <AppText mono variant="bodyMd">{inr(venue.fee)}</AppText>
      </View>
    </Pressable>
  );
}

function BookingRow({ booking, onRequestCancel, onConfirmCancel, onKeepCancel, confirming, cancelling, readOnly }: {
  booking: MyBooking; onRequestCancel: () => void; onConfirmCancel: () => void; onKeepCancel: () => void;
  confirming: boolean; cancelling: boolean; readOnly?: boolean;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const icon = booking.kind === 'AMENITY' ? amenityIcon(booking.title) : venueIcon(booking.title);
  const pillStatus = booking.statusLabel === 'CONFIRMED' ? 'approved' : venuePillStatus(booking.statusLabel);
  const canCancel = !readOnly && booking.statusLabel !== 'REJECTED';

  return (
    <Card>
      <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'flex-start' }}>
        <View style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: colors.canvas1, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={icon} size={20} color={colors.textSecondary} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="label" tone="tertiary">{booking.kind === 'AMENITY' ? t('mb_amenity') : t('mb_venue')}</AppText>
          <AppText variant="bodyMd" weight="500" style={{ marginTop: 2 }}>{booking.title}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{longDate(booking.when)}</AppText>
          {booking.subtitle ? <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>{booking.subtitle}</AppText> : null}
          {booking.fee > 0 ? <AppText variant="bodySm" tone="tertiary" mono style={{ marginTop: 2 }}>{inr(booking.fee)}</AppText> : null}
        </View>
        <Pill status={pillStatus} label={booking.statusLabel === 'CONFIRMED' ? 'CONFIRMED' : undefined} />
      </View>
      {canCancel ? (
        confirming ? (
          <View style={{ marginTop: space.s3 }}>
            <AppText variant="bodySm" tone="secondary">{t('mb_cancel_confirm')}</AppText>
            <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s2 }}>
              <Button
                title={t('mb_keep')}
                kind="secondary"
                onPress={onKeepCancel}
                style={{ flex: 1, minHeight: 36, paddingVertical: 6 }}
              />
              <Button
                title={t('mb_yes_cancel')}
                kind="destructive"
                loading={cancelling}
                onPress={onConfirmCancel}
                style={{ flex: 1, minHeight: 36, paddingVertical: 6 }}
              />
            </View>
          </View>
        ) : (
          <Button
            title={t('mb_cancel_booking')}
            kind="tertiary"
            loading={cancelling}
            onPress={onRequestCancel}
            style={{ marginTop: space.s3, alignSelf: 'flex-start', minHeight: 36, paddingVertical: 6 }}
          />
        )
      ) : null}
    </Card>
  );
}
